import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Box,
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { MainLayout } from 'views/layouts'
import { H2 } from 'views/components/UI/Text'
import ImportPeopleDialog from './import-people-dialog'
import CreateTeamFromProcessesDialog from './create-team-from-processes-dialog'
import ManualEvaluationSelectionDialog from './manual-evaluation-selection-dialog'
import TeamSourceProcesses from './team-source-processes'
import PersonFieldsCells, { getPersonUpdates, validPerformance } from './person-fields'
import getOneCompany from 'services/companies/getOneCompany'
import { useNotification } from 'lib/context/notification.context'
import {
  addLuminaTeamMembers,
  createLuminaTeam,
  deleteLuminaTeam,
  getEligibleLuminaMembers,
  getLuminaMemberEvaluations,
  getLuminaPeople,
  getLuminaTeam,
  getLuminaTeams,
  LuminaMember,
  LuminaPerson,
  LuminaTeam,
  LuminaTeamPayload,
  LuminaEvaluationSelection,
  LuminaMemberEvaluationHistory,
  updateLuminaTeam,
  updateLuminaTeamOrganization,
  updateLuminaPersonCargo,
  updateLuminaPersonDesempeno,
  syncTeamWithLumina,
  syncTeamWithNinebox,
  requestLuminaDescriptiveMapsInfo,
  requestLuminaIntegrationInfo,
} from './services'

interface TeamFormState {
  id?: number
  name: string
  description: string
  memberIds: number[]
}

type ManualAssociationAction =
  | { kind: 'team'; payload: LuminaTeamPayload; teamId?: number }
  | { kind: 'import-create'; payload: LuminaTeamPayload }
  | { kind: 'import-add'; teamId: number; memberIds: number[] }

interface PendingAssociation {
  action: ManualAssociationAction
  people: LuminaMemberEvaluationHistory[]
}

interface DescriptiveMapsCapability {
  number: string
  title: string
  description: string
}

const emptyForm: TeamFormState = { name: '', description: '', memberIds: [] }
const isProcessTeam = (team: LuminaTeam): boolean =>
  Array.isArray(team.source_processes) && team.source_processes.length > 0

const descriptiveMapsCapabilities: DescriptiveMapsCapability[] = [
  {
    number: '01',
    title: 'Visualiza el talento de tus equipos',
    description:
      'Construye mapas de talento que integran perfiles cognitivos, dominancias, talentos y competencias. Identifica fortalezas colectivas, brechas y oportunidades de desarrollo con indicadores que facilitan una visión integral de cada equipo.',
  },
  {
    number: '02',
    title: 'Identifica el potencial con matrices 9Box más ágiles',
    description:
      'Cruza desempeño y potencial para identificar talento clave, orientar planes de desarrollo y apoyar decisiones de sucesión. Personaliza tus matrices combinando los resultados de Betesa y 3D Skills, con ponderaciones ajustables según las necesidades de tu organización.',
  },
  {
    number: '03',
    title: 'Analiza y compara resultados con mayor profundidad',
    description:
      'Explora información consolidada de tu organización, consulta resultados por líder o profundiza en perfiles individuales. Compara personas de diferentes procesos para descubrir patrones, identificar oportunidades y tomar decisiones respaldadas por datos.',
  },
  {
    number: '04',
    title: 'Asegura la calidad de tu información',
    description:
      'Identifica automáticamente datos faltantes o inconsistencias, como evaluaciones de desempeño pendientes o resultados psicométricos no disponibles. Descarga los registros en Excel para facilitar su seguimiento y actualización.',
  },
  {
    number: '05',
    title: 'Comparte información estratégica de forma segura',
    description:
      'Facilita el acceso a los resultados mediante enlaces protegidos con contraseña. Comparte matrices 9Box y mapas de talento con líderes y responsables de gestión humana, promoviendo conversaciones y decisiones basadas en evidencia.',
  },
]

// Lumina opens a private map for the team owner.

interface OrganizationNodeProps {
  member: LuminaMember
  members: LuminaMember[]
  childrenByManager: Map<number | null, LuminaMember[]>
  getForbiddenManagerIds: (memberId: number) => Set<number>
  onManagerChange: (memberId: number, managerId: number | null) => void
}

const OrganizationNode = ({
  member,
  members,
  childrenByManager,
  getForbiddenManagerIds,
  onManagerChange,
}: OrganizationNodeProps) => {
  const children = childrenByManager.get(member.member_id) || []
  const forbiddenManagerIds = getForbiddenManagerIds(member.member_id)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 250, position: 'relative' }}>
      <Box
        sx={{
          width: 250,
          padding: 2,
          border: '1px solid #c9dcea',
          borderTop: '4px solid #2d749c',
          borderRadius: 2,
          background: '#fff',
          boxShadow: '0 8px 18px rgba(22, 50, 71, 0.1)',
        }}
      >
        <Typography fontWeight={700} color="#17374D">
          {member.full_name}
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap title={member.cargo || 'Sin cargo'}>
          {member.cargo?.trim() || 'Sin cargo'}
        </Typography>
        <FormControl fullWidth size="small" sx={{ marginTop: 1.5 }}>
          <InputLabel id={`manager-${member.member_id}`}>Reporta a</InputLabel>
          <Select
            labelId={`manager-${member.member_id}`}
            label="Reporta a"
            value={member.manager_member_id || ''}
            onChange={(event) =>
              onManagerChange(member.member_id, event.target.value ? Number(event.target.value) : null)
            }
          >
            <MenuItem value="">Sin coordinador</MenuItem>
            {members
              .filter((candidate) => !forbiddenManagerIds.has(candidate.member_id))
              .map((candidate) => (
                <MenuItem key={candidate.member_id} value={candidate.member_id}>
                  {candidate.full_name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Box>
      {children.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            gap: 3,
            paddingTop: 4,
            marginTop: 2,
            borderTop: '2px solid #9bc5dc',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -18,
              left: '50%',
              height: 18,
              borderLeft: '2px solid #9bc5dc',
            },
          }}
        >
          {children.map((child) => (
            <OrganizationNode
              key={child.member_id}
              member={child}
              members={members}
              childrenByManager={childrenByManager}
              getForbiddenManagerIds={getForbiddenManagerIds}
              onManagerChange={onManagerChange}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

const openLuminaLoadingWindow = (title: string) => {
  const launchWindow = window.open('', '_blank')
  if (!launchWindow) return null

  launchWindow.document.title = 'Bluesite Lumina'
  launchWindow.document.documentElement.lang = 'es'
  launchWindow.document.body.innerHTML = `
    <main style="min-height:100vh;display:grid;place-items:center;margin:0;padding:24px;background:linear-gradient(180deg,#f7fbfd 0%,#eef5f8 100%);font-family:Inter,Arial,sans-serif;color:#162236;box-sizing:border-box">
      <section style="display:flex;align-items:center;gap:16px;max-width:420px;padding:26px;border:1px solid rgba(255,255,255,.82);border-radius:24px;background:rgba(255,255,255,.92);box-shadow:0 28px 80px rgba(16,35,45,.14)">
        <span style="display:grid;place-items:center;width:42px;height:42px;border-radius:14px;background:#c6016b;color:#fff;font-size:22px;font-weight:800">✦</span>
        <span>
          <strong style="display:block;font-size:18px">Bluesite Lumina</strong>
          <span style="display:block;margin-top:4px;color:#5e6c76;font-size:14px">${title}</span>
        </span>
        <span aria-label="Cargando" style="width:22px;height:22px;margin-left:auto;border:3px solid #dce8ee;border-top-color:#c6016b;border-radius:50%;animation:spin .8s linear infinite"></span>
      </section>
    </main>
    <style>@keyframes spin { to { transform: rotate(360deg); } }</style>
  `

  return launchWindow
}

const normalizeTeamSearch = (value: string): string =>
  value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('es').trim()

const LuminaAnalytics = () => {
  // Build marker: staging integration verification.
  const { getError, getSuccess } = useNotification()
  const [tab, setTab] = useState<number>(1)
  const [teams, setTeams] = useState<LuminaTeam[]>([])
  const [teamSearch, setTeamSearch] = useState<string>('')
  const teamSearchTerm: string = normalizeTeamSearch(teamSearch)
  const filteredTeams: LuminaTeam[] = teams.filter((team) =>
    normalizeTeamSearch(team.name).includes(teamSearchTerm) ||
    team.source_processes?.some((process) => normalizeTeamSearch(process.name).includes(teamSearchTerm)))
  const [people, setPeople] = useState<LuminaPerson[]>([])
  const [peopleSearch, setPeopleSearch] = useState('')
  const [submittedPeopleSearch, setSubmittedPeopleSearch] = useState('')
  const [peopleLoading, setPeopleLoading] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [processTeamOpen, setProcessTeamOpen] = useState<boolean>(false)
  const [importedBatch, setImportedBatch] = useState(false)
  const [addTeamOpen, setAddTeamOpen] = useState(false)
  const [addTeamName, setAddTeamName] = useState('')
  const [existingTeam, setExistingTeam] = useState<LuminaTeam | null>(null)
  const [addingTeam, setAddingTeam] = useState(false)
  const [baremoDrafts, setBaremoDrafts] = useState<Record<number, string>>({})
  const [baremoErrors, setBaremoErrors] = useState<Record<number, string>>({})
  const [cargoDrafts, setCargoDrafts] = useState<Record<number, string>>({})
  const [members, setMembers] = useState<LuminaMember[]>([])
  const [memberCache, setMemberCache] = useState<Record<number, LuminaMember>>({})
  const [memberCargoDrafts, setMemberCargoDrafts] = useState<Record<number, string>>({})
  const [memberPerformanceDrafts, setMemberPerformanceDrafts] = useState<Record<number, string>>({})
  const memberRequestId = useRef<number>(0)
  const [memberSearch, setMemberSearch] = useState('')
  const [membersLoading, setMembersLoading] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [form, setForm] = useState<TeamFormState>(emptyForm)
  const selectedMembers: LuminaMember[] = form.memberIds.flatMap((id) => memberCache[id] ? [memberCache[id]] : [])
  const shownMembers: LuminaMember[] = [...selectedMembers, ...members.filter((member) => !form.memberIds.includes(member.user_id))]
  const invalidMemberPerformance: boolean = selectedMembers.some((member) => !validPerformance(memberPerformanceDrafts[member.user_id] || ''))
  const missingMemberPerformance: number = selectedMembers.filter((member) => !memberPerformanceDrafts[member.user_id]?.trim()).length
  const originalMemberIds = useRef<number[]>([])
  const [pendingAssociation, setPendingAssociation] = useState<PendingAssociation | null>(null)
  const [evaluationChoices, setEvaluationChoices] = useState<Record<string, number>>({})
  const [associationSaving, setAssociationSaving] = useState(false)
  const [organizationTeamId, setOrganizationTeamId] = useState<number | ''>('')
  const [organizationTeam, setOrganizationTeam] = useState<LuminaTeam | null>(null)
  const [organizationLoading, setOrganizationLoading] = useState(false)
  const [organizationSaving, setOrganizationSaving] = useState(false)
  const [syncingTeamId, setSyncingTeamId] = useState<number | null>(null)
  const [syncingNineboxTeamId, setSyncingNineboxTeamId] = useState<number | null>(null)
  const savingPersonFields = useRef<Set<string>>(new Set())
  const peopleRequestId = useRef(0)
  const { role, companyId } = useSelector((state: any) => ({ role: state.auth.role, companyId: state.user.companyId }))
  const userEmail = useSelector((state: any) => state.user.email || state.auth.email || '')
  const [companyData, setCompanyData] = useState<any>(null)
  const [requestingInfo, setRequestingInfo] = useState(false)
  // Fail closed: leaders must not see the module until the company capability is confirmed.
  const [descriptiveMapsEnabled, setDescriptiveMapsEnabled] = useState(false)

  useEffect(() => {
    if (companyId) {
      getOneCompany(companyId)
        .then((response: any) => {
          setCompanyData(response?.data || null)
          setDescriptiveMapsEnabled(response?.data?.descriptive_maps_enabled === true)
        })
        .catch(() => setDescriptiveMapsEnabled(false))
    }
  }, [companyId, role])

  const handleRequestDescriptiveMapsInfo = async () => {
    try {
      setRequestingInfo(true)
      await requestLuminaDescriptiveMapsInfo({
        mail: userEmail || 'solicitud@bluesitehr.com',
        type: 'lumina_descriptive_maps_request',
        company: companyData?.name || '',
        contact_name: companyData?.contact_name || '',
        contact_email: companyData?.contact_email || '',
        contact_phone: companyData?.phone || companyData?.contact_phone || '',
        internal_only: true,
      })
      getSuccess('Solicitud enviada correctamente.')
    } catch {
      getError('No fue posible enviar la solicitud. Inténtalo de nuevo.')
    } finally {
      setRequestingInfo(false)
    }
  }

  const handleRequestIntegrationInfo = async () => {
    try {
      setRequestingInfo(true)
      await requestLuminaIntegrationInfo({
        mail: 'sararuiz@bluesitehr.com',
        type: 'lumina_integration_request',
        company: companyData?.name || '',
        message: 'Solicitud de integración con otra plataforma para que Bluesite pueda consumir sus datos.',
        contact_name: companyData?.contact_name || '',
        contact_email: companyData?.contact_email || '',
        contact_phone: companyData?.phone || companyData?.contact_phone || '',
        internal_only: false,
      })
      getSuccess('Solicitud de integración enviada correctamente.')
    } catch {
      getError('No fue posible enviar la solicitud de integración. Inténtalo de nuevo.')
    } finally {
      setRequestingInfo(false)
    }
  }

  const loadTeams = useCallback(async () => {
    try {
      setLoading(true)
      setTeams(await getLuminaTeams())
    } catch {
      getError('No fue posible cargar los equipos.')
    } finally {
      setLoading(false)
    }
  }, [getError])

  const handleSyncTeam = async (team: LuminaTeam) => {
    // Abrimos la pestaña durante el gesto del usuario para evitar bloqueadores,
    // pero mostramos una carga de Lumina mientras el backend prepara el enlace privado.
    const launchWindow = openLuminaLoadingWindow('Estamos preparando tus analíticas…')
    try {
      setSyncingTeamId(team.id)
      const result = await syncTeamWithLumina(team.id)
      const launchUrl = result?.data?.launch_url || result?.launch_url
      if (!launchUrl) {
        launchWindow?.close()
        throw new Error('Mapas Descriptivos no devolvió un enlace privado para abrir el mapa.')
      }
      getSuccess(`El equipo “${team.name}” fue enviado a Mapas Descriptivos.`)
      if (launchWindow && !launchWindow.closed) {
        launchWindow.location.href = launchUrl
      } else {
        window.open(launchUrl, '_blank')
      }
    } catch (error: any) {
      launchWindow?.close()
      getError(
        error?.response?.data?.message ||
          error?.response?.data?.detail ||
          'No se pudo enviar el equipo a Mapas Descriptivos. Inténtalo de nuevo o contacta con soporte.'
      )
    } finally {
      setSyncingTeamId(null)
    }
  }

  const handleSyncNineboxTeam = async (team: LuminaTeam) => {
    const launchWindow = openLuminaLoadingWindow('Estamos preparando la matriz 9Box…')
    try {
      setSyncingNineboxTeamId(team.id)
      const result = await syncTeamWithNinebox(team.id)
      const launchUrl = result?.data?.launch_url || result?.launch_url
      if (!launchUrl) {
        launchWindow?.close()
        throw new Error('9Box no devolvió un enlace privado para abrir la matriz.')
      }
      getSuccess(`El equipo “${team.name}” fue enviado a 9Box.`)
      if (launchWindow && !launchWindow.closed) {
        launchWindow.location.href = launchUrl
      } else {
        window.open(launchUrl, '_blank')
      }
    } catch (error: any) {
      launchWindow?.close()
      getError(
        error?.response?.data?.message ||
          error?.response?.data?.detail ||
          'No se pudo generar el 9Box del equipo. Inténtalo de nuevo o contacta con soporte.'
      )
    } finally {
      setSyncingNineboxTeamId(null)
    }
  }

  useEffect(() => {
    if (!descriptiveMapsEnabled) {
      setTeams([])
      setLoading(false)
      return
    }

    loadTeams()
  }, [descriptiveMapsEnabled, loadTeams])

  const showPeople = useCallback((loadedPeople: LuminaPerson[]) => {
    setPeople(loadedPeople)
    setBaremoDrafts(
      Object.fromEntries(
        loadedPeople.map((person) => [person.user_id, person.desempeno == null ? '' : String(person.desempeno)])
      )
    )
    setCargoDrafts(Object.fromEntries(loadedPeople.map((person) => [person.user_id, person.cargo || ''])))
    setBaremoErrors({})
  }, [])

  const loadPeople = useCallback(
    async (search = '') => {
      const requestId = ++peopleRequestId.current
      try {
        setPeopleLoading(true)
        const loadedPeople = await getLuminaPeople(search)
        if (requestId === peopleRequestId.current) showPeople(loadedPeople)
      } catch {
        if (requestId === peopleRequestId.current) getError('No fue posible cargar las personas de Lumina.')
      } finally {
        if (requestId === peopleRequestId.current) setPeopleLoading(false)
      }
    },
    [getError, showPeople]
  )

  const handlePeopleImported = (importedPeople: LuminaPerson[]) => {
    peopleRequestId.current += 1
    setPeopleLoading(false)
    setPeopleSearch('')
    setSubmittedPeopleSearch('')
    setImportedBatch(importedPeople.length > 0)
    showPeople(importedPeople)
  }

  const searchPeople = () => {
    if (peopleLoading) return
    const search = peopleSearch.trim()
    setImportedBatch(false)
    setSubmittedPeopleSearch(search)
    if (!search) {
      peopleRequestId.current += 1
      showPeople([])
      return
    }
    void loadPeople(search)
  }

  const rememberMembers = useCallback((loaded: LuminaMember[]): void => {
    setMemberCache((current) => {
      const next: Record<number, LuminaMember> = { ...current }
      loaded.forEach((member) => { if (!next[member.user_id]) next[member.user_id] = member })
      return next
    })
    setMemberCargoDrafts((current) => {
      const next: Record<number, string> = { ...current }
      loaded.forEach((member) => { if (next[member.user_id] === undefined) next[member.user_id] = member.cargo || '' })
      return next
    })
    setMemberPerformanceDrafts((current) => {
      const next: Record<number, string> = { ...current }
      loaded.forEach((member) => { if (next[member.user_id] === undefined) next[member.user_id] = member.desempeno == null ? '' : String(member.desempeno) })
      return next
    })
  }, [])

  const loadEligibleMembers = useCallback(
    async (search = '') => {
      const requestId: number = ++memberRequestId.current
      try {
        setMembersLoading(true)
        const loaded: LuminaMember[] = await getEligibleLuminaMembers(search)
        if (requestId !== memberRequestId.current) return
        setMembers(loaded)
        rememberMembers(loaded)
      } catch {
        if (requestId === memberRequestId.current) getError('No fue posible cargar las personas elegibles.')
      } finally {
        if (requestId === memberRequestId.current) setMembersLoading(false)
      }
    },
    [getError, rememberMembers]
  )

  useEffect(() => {
    if (!dialogOpen || !memberSearch.trim()) {
      memberRequestId.current += 1
      setMembersLoading(false)
      return
    }
    const timeoutId = window.setTimeout(() => loadEligibleMembers(memberSearch.trim()), 300)
    return () => {
      window.clearTimeout(timeoutId)
      memberRequestId.current += 1
    }
  }, [dialogOpen, loadEligibleMembers, memberSearch])

  const loadOrganization = useCallback(
    async (teamId: number) => {
      try {
        setOrganizationLoading(true)
        setOrganizationTeam(await getLuminaTeam(teamId))
      } catch {
        getError('No fue posible cargar el organigrama del equipo.')
      } finally {
        setOrganizationLoading(false)
      }
    },
    [getError]
  )

  useEffect(() => {
    if (tab !== 2 || !organizationTeamId) return
    loadOrganization(organizationTeamId)
  }, [tab, organizationTeamId, loadOrganization])

  const openCreate = () => {
    setMemberSearch('')
    setMembers([])
    setMemberCache({})
    setMemberCargoDrafts({})
    setMemberPerformanceDrafts({})
    setForm(emptyForm)
    originalMemberIds.current = []
    setDialogOpen(true)
  }

  const openEdit = async (teamId: number) => {
    try {
      const team = await getLuminaTeam(teamId)
      setMemberSearch('')
      setMembers(team.members || [])
      setMemberCache(Object.fromEntries((team.members || []).map((member) => [member.user_id, member])))
      setMemberCargoDrafts(Object.fromEntries((team.members || []).map((member) => [member.user_id, member.cargo || ''])))
      setMemberPerformanceDrafts(Object.fromEntries((team.members || []).map((member) => [member.user_id, member.desempeno == null ? '' : String(member.desempeno)])))
      originalMemberIds.current = team.members?.map((member) => member.user_id) || []
      setForm({
        id: team.id,
        name: team.name,
        description: team.description || '',
        memberIds: team.members?.map((member) => member.user_id) || [],
      })
      setDialogOpen(true)
    } catch {
      getError('No fue posible cargar el equipo.')
    }
  }

  const toggleMember = (userId: number) =>
    setForm((current) => ({
      ...current,
      memberIds: current.memberIds.includes(userId)
        ? current.memberIds.filter((id) => id !== userId)
        : [...current.memberIds, userId],
    }))

  const findTeamWithName = async (name: string): Promise<LuminaTeam | undefined> => {
    const companyTeams = await getLuminaTeams()
    return companyTeams.find((team) => team.name.toLocaleLowerCase('es') === name.toLocaleLowerCase('es'))
  }

  const finishImportedTeam = async () => {
    setAddTeamOpen(false)
    setExistingTeam(null)
    setImportedBatch(false)
    showPeople([])
    setTab(1)
    await loadTeams()
  }

  const executeAssociation = async (action: ManualAssociationAction, selections: LuminaEvaluationSelection[]) => {
    if (action.kind === 'team') {
      const payload = { ...action.payload, evaluation_selections: selections }
      if (action.teamId) {
        await updateLuminaTeam(action.teamId, payload)
        getSuccess('Equipo actualizado correctamente.')
      } else {
        await createLuminaTeam(payload)
        getSuccess('Equipo creado correctamente.')
      }
      setDialogOpen(false)
      await loadTeams()
      return
    }
    if (action.kind === 'import-create') {
      await createLuminaTeam({ ...action.payload, evaluation_selections: selections })
      getSuccess('Equipo creado correctamente.')
    } else {
      const result = await addLuminaTeamMembers(action.teamId, action.memberIds, selections)
      getSuccess(result.added_count === 0
        ? 'Todas las personas ya pertenecían al equipo.'
        : `${result.added_count} ${result.added_count === 1 ? 'persona incorporada' : 'personas incorporadas'} al equipo.`)
    }
    await finishImportedTeam()
  }

  const prepareAssociation = async (action: ManualAssociationAction, newMemberIds: number[]) => {
    const isTeam = action.kind === 'team'
    try {
      if (isTeam) setSaving(true)
      else setAddingTeam(true)
      const histories: LuminaMemberEvaluationHistory[] = newMemberIds.length
        ? await getLuminaMemberEvaluations(newMemberIds) : []
      if (histories.some((person) => person.evaluations.length === 0)) {
        throw new Error('Una persona ya no tiene pruebas finalizadas disponibles. Actualiza la lista e inténtalo de nuevo.')
      }
      const peopleWithChoices = histories.filter((person) => [1, 2].some((categoryId) =>
        person.evaluations.filter((item) => item.category_id === categoryId).length > 1))
      if (peopleWithChoices.length) {
        setEvaluationChoices({})
        setPendingAssociation({ action, people: peopleWithChoices })
        return
      }
      await executeAssociation(action, [])
    } catch (error: any) {
      if (action.kind === 'import-create' && error?.response?.status === 409) {
        const team = await findTeamWithName(action.payload.name).catch(() => undefined)
        if (team) {
          setExistingTeam(team)
          return
        }
      }
      getError(error?.response?.data?.message || error?.message || 'No fue posible guardar el equipo.')
    } finally {
      if (isTeam) setSaving(false)
      else setAddingTeam(false)
    }
  }

  const confirmEvaluations = async () => {
    if (!pendingAssociation) return
    if (pendingAssociation.people.some((person) => [1, 2].some((categoryId) =>
      person.evaluations.filter((item) => item.category_id === categoryId).length > 1
      && !evaluationChoices[`${person.user_id}:${categoryId}`]))) return
    const selections: LuminaEvaluationSelection[] = pendingAssociation.people.map((person) => ({
      user_id: person.user_id,
      ...(person.evaluations.filter((item) => item.category_id === 1).length > 1
        ? { betesa_user_test_id: evaluationChoices[`${person.user_id}:1`] } : {}),
      ...(person.evaluations.filter((item) => item.category_id === 2).length > 1
        ? { skills_user_test_id: evaluationChoices[`${person.user_id}:2`] } : {}),
    }))
    try {
      setAssociationSaving(true)
      await executeAssociation(pendingAssociation.action, selections)
      setPendingAssociation(null)
    } catch (error: any) {
      getError(error?.response?.data?.message || 'No fue posible asociar las pruebas al equipo.')
    } finally {
      setAssociationSaving(false)
    }
  }

  const saveTeam = async () => {
    if (!form.name.trim()) {
      getError('El nombre del equipo es obligatorio.')
      return
    }
    if (invalidMemberPerformance || selectedMembers.length !== form.memberIds.length) {
      getError('Revisa las personas seleccionadas y su desempeño: de 1 a 10, con hasta dos decimales, o vacío.')
      return
    }
    const payload: LuminaTeamPayload = {
      name: form.name.trim(), description: form.description.trim(), member_ids: form.memberIds,
      person_updates: getPersonUpdates(selectedMembers, memberCargoDrafts, memberPerformanceDrafts),
    }
    const newMemberIds = form.memberIds.filter((id) => !originalMemberIds.current.includes(id))
    await prepareAssociation({ kind: 'team', payload, teamId: form.id }, newMemberIds)
  }

  const addImportedTeam = async () => {
    const name = addTeamName.trim()
    if (!name) {
      getError('El nombre del equipo es obligatorio.')
      return
    }
    if (!importedBatch || people.length === 0) return

    try {
      setAddingTeam(true)
      const team = await findTeamWithName(name)
      if (team) {
        setExistingTeam(team)
        return
      }
      const memberIds = people.map((person) => person.user_id)
      await prepareAssociation({ kind: 'import-create', payload: {
        name, description: '', member_ids: memberIds,
      } }, memberIds)
    } catch (error: any) {
      if (error?.response?.status === 409) {
        const team = await findTeamWithName(name).catch(() => undefined)
        if (team) {
          setExistingTeam(team)
          return
        }
      }
      getError(error?.response?.data?.message || 'No fue posible crear el equipo.')
    } finally {
      setAddingTeam(false)
    }
  }

  const confirmAddToExistingTeam = async () => {
    if (!existingTeam || !importedBatch || people.length === 0) return
    if (isProcessTeam(existingTeam)) {
      getError('No se pueden editar las personas del equipo porque vienen definidas por un proceso.')
      return
    }
    try {
      setAddingTeam(true)
      const team = await getLuminaTeam(existingTeam.id)
      if (isProcessTeam(team)) {
        getError('No se pueden editar las personas del equipo porque vienen definidas por un proceso.')
        return
      }
      const memberIds = people.map((person) => person.user_id)
      const newMemberIds = memberIds.filter((id) => !team.members?.some((member) => member.user_id === id))
      await prepareAssociation({ kind: 'import-add', teamId: existingTeam.id, memberIds }, newMemberIds)
    } catch (error: any) {
      if (error?.response?.status === 404) setExistingTeam(null)
      getError(error?.response?.data?.message || 'No fue posible incorporar las personas al equipo.')
    } finally {
      setAddingTeam(false)
    }
  }

  const removeTeam = async (team: LuminaTeam) => {
    if (!window.confirm(`Se eliminará definitivamente el equipo “${team.name}”.`)) return
    try {
      await deleteLuminaTeam(team.id)
      if (organizationTeamId === team.id) {
        setOrganizationTeamId('')
        setOrganizationTeam(null)
      }
      getSuccess('Equipo eliminado correctamente.')
      await loadTeams()
    } catch {
      getError('No fue posible eliminar el equipo.')
    }
  }

  const teamMembers = organizationTeam?.members
  const organizationMembers = useMemo(() => teamMembers || [], [teamMembers])
  const childrenByManager = useMemo(() => {
    const children = new Map<number | null, LuminaMember[]>()
    organizationMembers.forEach((member) => {
      const managerId = member.manager_member_id || null
      children.set(managerId, [...(children.get(managerId) || []), member])
    })
    return children
  }, [organizationMembers])
  const organizationRoots = childrenByManager.get(null) || []
  const getForbiddenManagerIds = (memberId: number) => {
    const forbiddenIds = new Set([memberId])
    const pendingIds = [memberId]

    while (pendingIds.length > 0) {
      const currentId = pendingIds.pop() as number
      ;(childrenByManager.get(currentId) || []).forEach((child) => {
        forbiddenIds.add(child.member_id)
        pendingIds.push(child.member_id)
      })
    }

    return forbiddenIds
  }

  const changeManager = (memberId: number, managerId: number | null) => {
    setOrganizationTeam((current) =>
      current
        ? {
            ...current,
            members: (current.members || []).map((member) =>
              member.member_id === memberId ? { ...member, manager_member_id: managerId } : member
            ),
          }
        : current
    )
  }

  const saveOrganization = async () => {
    if (!organizationTeam) return
    try {
      setOrganizationSaving(true)
      const team = await updateLuminaTeamOrganization(organizationTeam.id, {
        relationships: (organizationTeam.members || []).map((member) => ({
          member_id: member.member_id,
          manager_member_id: member.manager_member_id || null,
        })),
      })
      setOrganizationTeam(team)
      getSuccess('Organigrama guardado correctamente.')
    } catch (error: any) {
      getError(error?.response?.data?.message || 'No fue posible guardar el organigrama.')
    } finally {
      setOrganizationSaving(false)
    }
  }

  const saveBaremo = async (person: LuminaPerson) => {
    const value = baremoDrafts[person.user_id]?.trim() || ''
    const numericValue = value === '' ? null : Number(value.replace(',', '.'))
    const currentValue = person.desempeno == null ? null : Number(person.desempeno)
    const fieldKey = `${person.user_id}:desempeno`
    const isValidValue =
      numericValue === null ||
      (Number.isFinite(numericValue) &&
        numericValue >= 1 &&
        numericValue <= 10 &&
        /^\d{1,2}([.,]\d{1,2})?$/.test(value))
    if (!isValidValue) {
      setBaremoErrors((current) => ({
        ...current,
        [person.user_id]: 'Debe estar entre 1,00 y 10,00, con un máximo de dos decimales.',
      }))
      getError('El desempeño debe ser un número entre 1,00 y 10,00 con un máximo de dos decimales.')
      return
    }

    if (numericValue === currentValue || savingPersonFields.current.has(fieldKey)) return

    try {
      savingPersonFields.current.add(fieldKey)
      await updateLuminaPersonDesempeno(person.user_id, numericValue)
      setPeople((current) =>
        current.map((item) => (item.user_id === person.user_id ? { ...item, desempeno: numericValue } : item))
      )
      setBaremoErrors((current) => ({ ...current, [person.user_id]: '' }))
      getSuccess('Desempeño guardado correctamente.')
    } catch (error: any) {
      getError(error?.response?.data?.message || 'No fue posible guardar el desempeño.')
    } finally {
      savingPersonFields.current.delete(fieldKey)
    }
  }

  const saveCargo = async (person: LuminaPerson) => {
    const cargo = cargoDrafts[person.user_id]?.trim() || ''
    const fieldKey = `${person.user_id}:cargo`
    if (cargo === (person.cargo || '').trim() || savingPersonFields.current.has(fieldKey)) return

    try {
      savingPersonFields.current.add(fieldKey)
      await updateLuminaPersonCargo(person.user_id, cargo)
      setPeople((current) =>
        current.map((item) => (item.user_id === person.user_id ? { ...item, cargo: cargo || null } : item))
      )
      getSuccess('Cargo guardado correctamente.')
    } catch (error: any) {
      getError(error?.response?.data?.message || 'No fue posible guardar el cargo.')
    } finally {
      savingPersonFields.current.delete(fieldKey)
    }
  }

  if (!descriptiveMapsEnabled) {
    return (
      <MainLayout>
        <H2>Mapas Descriptivos</H2>
        <Typography role="note" sx={{ mb: 2, color: 'text.secondary' }}>
          Los equipos creados desde procesos conservan los resultados del origen elegido. En los equipos manuales se elige
          la prueba finalizada y activa por tipo cuando hay varias; si solo hay una, se asocia automáticamente.
        </Typography>
        <Box
          sx={{
            maxWidth: 1120,
            mx: 'auto',
            mt: { xs: 3, md: 6 },
            overflow: 'hidden',
            background: '#fff',
            border: '1px solid #e1eaf2',
            borderRadius: '20px',
            boxShadow: '0 16px 40px rgba(22, 50, 71, 0.08)',
          }}
        >
          <Box
            sx={{
              px: { xs: 3, md: 7 },
              py: { xs: 5, md: 7 },
              textAlign: 'center',
              background: 'linear-gradient(135deg, #f7fbff 0%, #fff7fc 100%)',
              borderBottom: '1px solid #e1eaf2',
            }}
          >
            <Typography
              sx={{
                mb: 1.5,
                color: '#176fc1',
                fontSize: '0.75rem',
                fontWeight: 800,
                letterSpacing: '0.14em',
              }}
            >
              MAPAS DESCRIPTIVOS
            </Typography>
            <Typography
              component="h2"
              sx={{
                maxWidth: 850,
                mx: 'auto',
                mb: 2,
                color: '#c90072',
                fontSize: { xs: '2.15rem', md: '3.15rem' },
                fontWeight: 800,
                lineHeight: 1.08,
                letterSpacing: '-0.035em',
                background: 'linear-gradient(110deg, #c90072 0%, #a90086 42%, #176fc1 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Analítica Avanzada de Talento
            </Typography>
            <Typography
              sx={{
                maxWidth: 760,
                mx: 'auto',
                mb: 2.5,
                color: '#17374d',
                fontSize: { xs: '1.1rem', md: '1.35rem' },
                fontWeight: 700,
                lineHeight: 1.4,
              }}
            >
              Transforma los datos de tu talento en decisiones estratégicas.
            </Typography>
            <Box sx={{ mb: 2.5 }}>
              <Button
                variant="contained"
                onClick={handleRequestDescriptiveMapsInfo}
                disabled={requestingInfo}
                sx={{ px: 4, py: 1.25, fontWeight: 700 }}
              >
                {requestingInfo ? 'Enviando…' : 'Solicitar información'}
              </Button>
            </Box>
            <Typography sx={{ maxWidth: 810, mx: 'auto', color: '#526779', fontSize: '1rem', lineHeight: 1.7 }}>
              Lleva el análisis de talento a otro nivel. Integra resultados, compara perfiles y descubre oportunidades
              de desarrollo, movilidad y sucesión a través de herramientas visuales e interactivas que facilitan
              decisiones más objetivas y estratégicas.
            </Typography>
          </Box>

          <Box sx={{ px: { xs: 3, md: 7 }, py: { xs: 5, md: 6 } }}>
            <Typography
              component="h2"
              sx={{ mb: 3.5, color: '#17374d', fontSize: { xs: '1.5rem', md: '1.9rem' }, fontWeight: 800 }}
            >
              ¿Qué puedes hacer con este módulo?
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' },
                gap: 2,
              }}
            >
              {descriptiveMapsCapabilities.map((capability) => (
                <Box
                  key={capability.number}
                  sx={{
                    minHeight: 240,
                    p: 3,
                    border: '1px solid #dce8f1',
                    borderRadius: 3,
                    background: '#fff',
                    transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      borderColor: '#b5d1e4',
                      boxShadow: '0 12px 24px rgba(22, 50, 71, 0.1)',
                    },
                  }}
                >
                  <Typography
                    sx={{ mb: 2, color: '#c90072', fontSize: '0.8rem', fontWeight: 800, letterSpacing: '0.1em' }}
                  >
                    {capability.number}
                  </Typography>
                  <Typography
                    component="h3"
                    sx={{ mb: 1.5, color: '#17374d', fontSize: '1.1rem', fontWeight: 800, lineHeight: 1.35 }}
                  >
                    {capability.title}
                  </Typography>
                  <Typography sx={{ color: '#526779', fontSize: '0.94rem', lineHeight: 1.65 }}>
                    {capability.description}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                mt: { xs: 4, md: 5 },
                px: { xs: 3, md: 6 },
                py: { xs: 4, md: 5 },
                textAlign: 'center',
                background: 'linear-gradient(110deg, #fff4fa 0%, #f3f9ff 100%)',
                borderRadius: 3,
              }}
            >
              <Typography
                sx={{
                  mb: 1.5,
                  color: '#c90072',
                  fontSize: { xs: '1.45rem', md: '1.8rem' },
                  fontWeight: 800,
                  lineHeight: 1.25,
                }}
              >
                Más información. Mejores decisiones. Mayor impacto.
              </Typography>
              <Typography sx={{ maxWidth: 730, mx: 'auto', color: '#526779', fontSize: '1rem', lineHeight: 1.7 }}>
                Convierte los resultados de evaluación en una visión integral del talento y descubre nuevas
                posibilidades para desarrollar el potencial de tu organización.
              </Typography>
              <Button
                variant="contained"
                onClick={handleRequestDescriptiveMapsInfo}
                disabled={requestingInfo}
                sx={{ mt: 3.5, px: 4, py: 1.25, fontWeight: 700 }}
              >
                {requestingInfo ? 'Enviando…' : 'Solicitar información'}
              </Button>
            </Box>
          </Box>
        </Box>
      </MainLayout>
    )
  }

  return (
    <MainLayout>
      <Box sx={{ padding: '2% 0', width: '100%' }}>
        <H2 marginBottom="24px">Mapas Descriptivos</H2>
        <Typography role="note" sx={{ mb: 2, color: 'text.secondary' }}>
          Los equipos creados desde procesos conservan los resultados del origen elegido. En los equipos manuales se elige
          la prueba finalizada y activa por tipo cuando hay varias; si solo hay una, se asocia automáticamente.
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            flexDirection: { xs: 'column', md: 'row' },
            mb: 3,
            p: 2,
            border: '1px solid #c9dcea',
            borderRadius: 2,
            background: 'linear-gradient(100deg, rgba(239, 245, 251, 0.95), rgba(255, 255, 255, 0.98))',
          }}
        >
          <Box>
            <Typography fontWeight={700} color="#17374D">
              ¿Quieres conectar esta información con otra plataforma?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Solicita una integración para que Bluesite pueda consumir sus datos y ampliar tus análisis.
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={handleRequestIntegrationInfo}
            disabled={requestingInfo}
            sx={{ flexShrink: 0, fontWeight: 700, textTransform: 'none' }}
          >
            {requestingInfo ? 'Enviando…' : 'Solicitar integración'}
          </Button>
        </Box>

        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          sx={{
            borderBottom: '1px solid #d7e0ea',
            marginBottom: 3,
            '& .MuiTabs-indicator': { backgroundColor: '#c6016b' },
          }}
        >
          <Tab label="Equipos" value={1} sx={{ color: '#163247', fontFamily: 'Public Sans', fontWeight: 700 }} />
          <Tab label="Organigrama" value={2} sx={{ color: '#163247', fontFamily: 'Public Sans', fontWeight: 700 }} />
          <Tab label="Personas" value={0} sx={{ color: '#163247', fontFamily: 'Public Sans', fontWeight: 700 }} />
        </Tabs>

        {tab === 0 && (
          <Box sx={{ p: 1.5, borderRadius: '10px', background: '#fff', boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)' }}>
            {importOpen && <ImportPeopleDialog onClose={() => setImportOpen(false)} onImported={handlePeopleImported} />}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'minmax(0, 1fr)',
                  sm: 'minmax(0, 1fr) auto',
                  xl: 'minmax(0, 1fr) minmax(360px, 520px) auto',
                },
                alignItems: 'center',
                gap: 2,
                mb: 2,
                px: 1,
                pt: 0.5,
              }}
            >
              <Typography color="text.secondary" sx={{ gridColumn: { xs: '1', sm: '1 / -1', xl: 'auto' }, fontSize: '0.875rem', lineHeight: 1.3 }}>
                Personas de la empresa con BETESA y/o 3D Skills finalizada.
                <br />
                El desempeño se guarda para toda la empresa.
              </Typography>
              <Box component="form" onSubmit={(event) => { event.preventDefault(); searchPeople() }} sx={{ display: 'flex', gap: 1, minWidth: 0 }}>
                <TextField
                  fullWidth
                  label="Buscar por nombre, correo o documento"
                  value={peopleSearch}
                  onChange={(event) => setPeopleSearch(event.target.value)}
                  sx={{ minWidth: 0, backgroundColor: '#fff', '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
                />
                <Button
                  type="submit"
                  variant="outlined"
                  disabled={peopleLoading}
                  sx={{ flexShrink: 0, borderColor: '#2d749c', color: '#2d749c', fontWeight: 700, textTransform: 'none', '&:hover': { borderColor: '#235f81', background: '#f2f8fb' } }}
                >
                  Buscar
                </Button>
              </Box>
              <Button
                variant="contained"
                onClick={() => setImportOpen(true)}
                sx={{
                  flexShrink: 0,
                  justifySelf: { xs: 'stretch', sm: 'end' },
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #c6016b 0%, #8f215d 100%)',
                  boxShadow: '0 14px 24px rgba(198, 1, 107, 0.2)',
                  fontFamily: 'Public Sans',
                  fontWeight: 700,
                  textTransform: 'none',
                  '&:hover': { background: 'linear-gradient(135deg, #b00060 0%, #7b1c50 100%)' },
                }}
              >
                Importar Excel
              </Button>
            </Box>
            {importedBatch && people.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 2.5, mb: 2, px: 2.5, py: 2, border: '1px solid #e7c5d9', borderRadius: '16px', background: '#fff7fb' }}>
                <Button
                  variant="contained"
                  onClick={() => { setAddTeamName(''); setExistingTeam(null); setAddTeamOpen(true) }}
                  sx={{ flexShrink: 0, borderRadius: '12px', background: 'linear-gradient(135deg, #c6016b 0%, #8f215d 100%)', boxShadow: '0 14px 24px rgba(198, 1, 107, 0.2)', fontFamily: 'Public Sans', fontWeight: 700, textTransform: 'none', '&:hover': { background: 'linear-gradient(135deg, #b00060 0%, #7b1c50 100%)' } }}
                >
                  Agregar personas a un equipo
                </Button>
                <Box>
                  <Typography fontWeight={700} color="#79214e">
                    {people.length} {people.length === 1 ? 'persona importada correctamente' : 'personas importadas correctamente'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Puedes crear un equipo con las personas que aparecen en esta lista.
                  </Typography>
                </Box>
              </Box>
            )}
            {peopleLoading ? (
              <Box display="flex" justifyContent="center" padding={6}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns:
                      '105px minmax(240px, 1.2fr) minmax(220px, 1.15fr) minmax(240px, 1.25fr) 160px 160px',
                    gridTemplateAreas: '"baremo name cargo email document tests"',
                    gap: 2,
                    padding: '16px 20px',
                    background: '#eff5fb',
                    borderRadius: '12px',
                    color: '#163247',
                    fontWeight: 700,
                    letterSpacing: '0.01em',
                    lineHeight: 1.2,
                    minWidth: 1000,
                    mb: 1.5,
                  }}
                >
                  <Typography sx={{ gridArea: 'name', color: '#17374D', fontSize: '0.875rem', fontWeight: 700 }}>
                    Nombre
                  </Typography>
                  <Typography sx={{ gridArea: 'email', color: '#17374D', fontSize: '0.875rem', fontWeight: 700 }}>
                    Correo
                  </Typography>
                  <Typography sx={{ gridArea: 'cargo', color: '#17374D', fontSize: '0.875rem', fontWeight: 700 }}>
                    Cargo
                  </Typography>
                  <Typography sx={{ gridArea: 'document', color: '#17374D', fontSize: '0.875rem', fontWeight: 700 }}>
                    Documento
                  </Typography>
                  <Typography sx={{ gridArea: 'tests', color: '#17374D', fontSize: '0.875rem', fontWeight: 700 }}>
                    Pruebas realizadas
                  </Typography>
                  <Typography sx={{ gridArea: 'baremo', color: '#17374D', fontSize: '0.875rem', fontWeight: 700 }}>
                    Desempeño
                  </Typography>
                </Box>
                {people.length === 0 ? (
                  <Box sx={{ px: 2.5, py: 2, borderTop: '1px solid #e5e7eb' }}>
                    <Typography sx={{ color: '#536b84', fontSize: '0.875rem' }}>
                      {submittedPeopleSearch
                        ? 'No se encontraron personas elegibles.'
                        : 'Escribe un nombre, correo o documento para buscar personas.'}
                    </Typography>
                  </Box>
                ) : (
                  people.map((person) => (
                    <Box
                      key={person.user_id}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns:
                          '105px minmax(240px, 1.2fr) minmax(220px, 1.15fr) minmax(240px, 1.25fr) 160px 160px',
                        gridTemplateAreas: '"baremo name cargo email document tests"',
                        gap: 2,
                        alignItems: 'center',
                        padding: '6px 20px',
                        borderTop: '1px solid #e5e7eb',
                        minWidth: 1000,
                      }}
                    >
                      <Typography
                        noWrap
                        fontWeight={600}
                        sx={{ gridArea: 'name', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.875rem' }}
                        title={person.full_name}
                      >
                        {person.full_name}
                      </Typography>
                      <TextField
                        size="small"
                        type="text"
                        value={cargoDrafts[person.user_id] || ''}
                        onChange={(event) =>
                          setCargoDrafts((current) => ({ ...current, [person.user_id]: event.target.value }))
                        }
                        onBlur={() => saveCargo(person)}
                        placeholder="Cargo"
                        inputProps={{ maxLength: 500 }}
                        sx={{ gridArea: 'cargo', width: '100%', backgroundColor: '#fff', '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
                      />
                      <Typography
                        noWrap
                        color="text.secondary"
                        sx={{ gridArea: 'email', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.875rem' }}
                        title={person.email || 'Sin correo'}
                      >
                        {person.email || 'Sin correo'}
                      </Typography>
                      <Typography
                        noWrap
                        color="text.secondary"
                        sx={{ gridArea: 'document', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.875rem' }}
                        title={person.document || 'Sin documento'}
                      >
                        {person.document || 'Sin documento'}
                      </Typography>
                      <Typography
                        noWrap
                        color="text.secondary"
                        sx={{ gridArea: 'tests', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.875rem' }}
                      >
                        {person.completed_tests.join(', ')}
                      </Typography>
                      <TextField
                        size="small"
                        type="text"
                        inputProps={{ inputMode: 'decimal' }}
                        value={baremoDrafts[person.user_id] || ''}
                        onChange={(event) => {
                          const nextValue = event.target.value
                          const numericValue = Number(nextValue.replace(',', '.'))
                          const validFormat = /^\d{0,2}([.,]\d{0,2})?$/.test(nextValue)
                          if (
                            !validFormat ||
                            (nextValue !== '' && Number.isFinite(numericValue) && numericValue > 10)
                          ) {
                            setBaremoErrors((current) => ({
                              ...current,
                              [person.user_id]: 'Solo se permiten valores de 1,00 a 10,00.',
                            }))
                            return
                          }
                          setBaremoDrafts((current) => ({ ...current, [person.user_id]: nextValue }))
                          setBaremoErrors((current) => ({ ...current, [person.user_id]: '' }))
                        }}
                        onBlur={() => saveBaremo(person)}
                        error={Boolean(baremoErrors[person.user_id])}
                        helperText={baremoErrors[person.user_id] || undefined}
                        placeholder="1–10"
                        sx={{ gridArea: 'baremo', width: 78, backgroundColor: '#fff', '& .MuiInputBase-input': { fontSize: '0.875rem', px: 1 } }}
                      />
                    </Box>
                  ))
                )}
              </Box>
            )}
          </Box>
        )}

        {tab === 1 && (
          <Box sx={{ p: 1.5, borderRadius: '10px', background: '#fff', boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) auto', xl: 'minmax(240px, 1fr) minmax(220px, 300px) auto' }, alignItems: 'center', gap: 2, mb: 2, px: 1, pt: 0.5 }}>
              <Typography color="text.secondary" sx={{ fontSize: '0.875rem', gridColumn: { xs: '1', md: '1 / -1', xl: 'auto' } }}>
                Agrupa personas con BETESA o 3D Skills finalizada para preparar sus analíticas.
              </Typography>
              <TextField
                label="Buscar equipos"
                placeholder="Nombre del equipo o proceso"
                type="search"
                size="small"
                value={teamSearch}
                onChange={(event) => setTeamSearch(event.target.value)}
                sx={{ minWidth: 0, width: '100%' }}
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, flexShrink: 0 }}>
              <Button variant="outlined" onClick={() => setProcessTeamOpen(true)} sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700, color: '#9b165a', borderColor: '#c6016b' }}>
                Crear equipo desde procesos
              </Button>
              <Button
                variant="contained"
                onClick={openCreate}
                sx={{
                  flexShrink: 0,
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #c6016b 0%, #8f215d 100%)',
                  boxShadow: '0 14px 24px rgba(198, 1, 107, 0.2)',
                  fontFamily: 'Public Sans',
                  fontWeight: 700,
                  textTransform: 'none',
                  '&:hover': { background: 'linear-gradient(135deg, #b00060 0%, #7b1c50 100%)' },
                }}
              >
                Crear equipo
              </Button>
              </Box>
            </Box>
            {loading ? (
              <Box display="flex" justifyContent="center" padding={6}>
                <CircularProgress />
              </Box>
            ) : filteredTeams.length === 0 ? (
              <Box sx={{ px: 2.5, py: 2, borderTop: '1px solid #e5e7eb' }}>
                <Typography sx={{ color: '#536b84', fontSize: '0.875rem' }}>{teams.length ? 'No hay equipos que coincidan con la búsqueda.' : 'No hay equipos creados todavía.'}</Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table sx={{ minWidth: 900, borderCollapse: 'separate', borderSpacing: 0 }}>
                  <TableHead>
                    <TableRow sx={{
                      '& th': { background: '#eff5fb', color: '#163247', fontSize: '0.9375rem', fontWeight: 700, letterSpacing: '0.01em', lineHeight: 1.2, px: 2.5, py: 2, borderBottom: 0 },
                      '& th:first-of-type': { borderTopLeftRadius: '12px', borderBottomLeftRadius: '12px' },
                      '& th:last-of-type': { borderTopRightRadius: '12px', borderBottomRightRadius: '12px' },
                    }}>
                      <TableCell>Equipo</TableCell>
                      <TableCell>Descripción</TableCell>
                      <TableCell sx={{ minWidth: 200 }}>Proceso(s) de origen</TableCell>
                      <TableCell>Personas</TableCell>
                      <TableCell align="left">
                        Acciones
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody sx={{
                    '& td': { color: '#536b84', fontSize: '0.875rem', px: 2.5, py: 0.75, borderBottom: '1px solid #e5e7eb' },
                    '& td:first-of-type': { color: '#1f344d' },
                    '& tr:last-of-type td': { borderBottom: 0 },
                  }}>
                    {filteredTeams.map((team) => (
                      <TableRow key={team.id} hover>
                        <TableCell>{team.name}</TableCell>
                        <TableCell>{team.description || 'Sin descripción'}</TableCell>
                        <TableCell sx={{ minWidth: 200, maxWidth: 340 }}>
                          <TeamSourceProcesses processes={team.source_processes} />
                        </TableCell>
                        <TableCell>{team.member_count || 0}</TableCell>
                        <TableCell align="left">
                          <Box display="flex" justifyContent="flex-start" gap={1}>
                            <Button
                              size="small"
                              onClick={() => handleSyncTeam(team)}
                              disabled={syncingTeamId === team.id}
                              sx={{
                                color: '#2d749c',
                                fontFamily: 'Public Sans',
                                fontWeight: 700,
                                textTransform: 'none',
                              }}
                            >
                              {syncingTeamId === team.id ? 'Enviando...' : 'Mapas Descriptivos'}
                            </Button>
                            <Button
                              size="small"
                              onClick={() => handleSyncNineboxTeam(team)}
                              disabled={syncingNineboxTeamId === team.id}
                              sx={{
                                color: '#2d749c',
                                fontFamily: 'Public Sans',
                                fontWeight: 700,
                                textTransform: 'none',
                              }}
                            >
                              {syncingNineboxTeamId === team.id ? 'Enviando...' : '9Box'}
                            </Button>
                            <Button
                              size="small"
                              onClick={() => {
                                setOrganizationTeamId(team.id)
                                setTab(2)
                              }}
                              sx={{
                                color: '#2d749c',
                                fontFamily: 'Public Sans',
                                fontWeight: 700,
                                textTransform: 'none',
                              }}
                            >
                              Organigrama
                            </Button>
                            <Tooltip title={isProcessTeam(team) ? 'No se pueden editar las personas del equipo porque vienen definidas por un proceso.' : ''}>
                              <span>
                                <Button
                                  size="small"
                                  onClick={() => openEdit(team.id)}
                                  disabled={isProcessTeam(team)}
                                  sx={{
                                    color: '#2d749c',
                                    fontFamily: 'Public Sans',
                                    fontWeight: 700,
                                    textTransform: 'none',
                                  }}
                                >
                                  Editar
                                </Button>
                              </span>
                            </Tooltip>
                            <Button
                              size="small"
                              color="error"
                              onClick={() => removeTeam(team)}
                              sx={{ fontFamily: 'Public Sans', fontWeight: 700, textTransform: 'none' }}
                            >
                              Eliminar
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {tab === 2 && (
          <Box>
            <Typography color="text.secondary" marginBottom={2}>
              Define visualmente quién reporta a quién. Una persona puede coordinar a tantas personas como sea
              necesario.
            </Typography>
            <FormControl sx={{ minWidth: 320, marginBottom: 3 }}>
              <InputLabel id="lumina-team-select">Equipo</InputLabel>
              <Select
                labelId="lumina-team-select"
                label="Equipo"
                value={organizationTeamId}
                onChange={(event) => setOrganizationTeamId(Number(event.target.value))}
              >
                {teams.map((team) => (
                  <MenuItem key={team.id} value={team.id}>
                    {team.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {!organizationTeamId ? (
              <Box sx={{ padding: 4, border: '1px solid #d7e0ea', borderRadius: 2, background: '#fff' }}>
                <Typography>Selecciona un equipo para crear su organigrama.</Typography>
              </Box>
            ) : organizationLoading ? (
              <Box display="flex" justifyContent="center" padding={6}>
                <CircularProgress />
              </Box>
            ) : organizationMembers.length === 0 ? (
              <Box sx={{ padding: 4, border: '1px solid #d7e0ea', borderRadius: 2, background: '#fff' }}>
                <Typography>Este equipo no tiene personas asignadas.</Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  border: '1px solid #d7e0ea',
                  borderRadius: 3,
                  background: '#f7fbff',
                  overflow: 'auto',
                  padding: 3,
                }}
              >
                <Box display="flex" justifyContent="flex-end" marginBottom={3}>
                  <Button
                    variant="contained"
                    disabled={organizationSaving}
                    onClick={saveOrganization}
                    sx={{
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #c6016b 0%, #8f215d 100%)',
                      textTransform: 'none',
                    }}
                  >
                    {organizationSaving ? 'Guardando...' : 'Guardar organigrama'}
                  </Button>
                </Box>
                <Box display="flex" justifyContent="center" gap={4} minWidth="max-content" paddingBottom={2}>
                  {organizationRoots.map((member) => (
                    <OrganizationNode
                      key={member.member_id}
                      member={member}
                      members={organizationMembers}
                      childrenByManager={childrenByManager}
                      getForbiddenManagerIds={getForbiddenManagerIds}
                      onManagerChange={changeManager}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>

      <Dialog open={addTeamOpen} onClose={() => { if (!addingTeam) { setAddTeamOpen(false); setExistingTeam(null) } }} fullWidth maxWidth="sm">
        <DialogTitle>{existingTeam ? 'Incorporar al equipo existente' : 'Agregar equipo'}</DialogTitle>
        <DialogContent>
          {existingTeam ? (
            <>
              <Typography marginBottom={1}>
                El equipo <strong>{existingTeam.name}</strong> ya existe. ¿Quieres incorporar {people.length === 1 ? 'a la persona importada' : `a las ${people.length} personas importadas`}?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isProcessTeam(existingTeam)
                  ? 'No se pueden editar las personas del equipo porque vienen definidas por un proceso.'
                  : 'Se conservarán sus integrantes actuales. Quienes ya pertenezcan al equipo no se duplicarán.'}
              </Typography>
            </>
          ) : (
            <>
              <Typography color="text.secondary" marginBottom={0.5}>
                {people.length} {people.length === 1 ? 'persona importada formará' : 'personas importadas formarán'} parte del equipo.
              </Typography>
              <Typography variant="body2" color="text.secondary" marginBottom={2}>
                Escribe el nombre de un equipo nuevo o de uno existente. Si ya existe, te pediremos confirmación antes de añadir las personas importadas.
              </Typography>
              <TextField
                autoFocus
                fullWidth
                label="Nombre del equipo"
                value={addTeamName}
                onChange={(event) => setAddTeamName(event.target.value)}
                inputProps={{ maxLength: 150 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setAddTeamOpen(false); setExistingTeam(null) }} disabled={addingTeam}>Cancelar</Button>
          {existingTeam && <Button onClick={() => setExistingTeam(null)} disabled={addingTeam}>Cambiar nombre</Button>}
          <Button
            variant="contained"
            onClick={existingTeam ? confirmAddToExistingTeam : addImportedTeam}
            disabled={addingTeam || (existingTeam ? isProcessTeam(existingTeam) : !addTeamName.trim())}
            sx={{
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #c6016b 0%, #8f215d 100%)',
              color: '#fff',
              fontFamily: 'Public Sans',
              fontWeight: 700,
              textTransform: 'none',
              '&:hover': { background: 'linear-gradient(135deg, #b00060 0%, #7b1c50 100%)' },
              '&.Mui-disabled': { background: '#e8edf2', color: '#52657a', boxShadow: 'none' },
            }}
          >
            {addingTeam ? (existingTeam ? 'Incorporando…' : 'Creando…') : (existingTeam ? 'Sí, incorporar' : 'Crear equipo')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogOpen}
        onClose={() => !saving && setDialogOpen(false)}
        fullScreen
        PaperProps={{ sx: { backgroundColor: '#f7fbff' } }}
      >
        <DialogTitle>{form.id ? 'Editar equipo' : 'Crear equipo'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Nombre"
            value={form.name}
            onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Descripción"
            value={form.description}
            onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
            margin="normal"
            multiline
            minRows={2}
          />
          <Typography fontWeight={700} marginTop={3}>
            Personas elegibles
          </Typography>
          <Typography variant="body2" color="text.secondary" marginBottom={1}>
            Solo se muestran personas de la compañía con BETESA o 3D Skills finalizada.
          </Typography>
          <Typography variant="body2" color="text.secondary" marginBottom={1}>
            Escribe para buscar; no se cargan todos los trabajadores automáticamente.
          </Typography>
          <TextField
            fullWidth
            label="Buscar por nombre, correo o documento"
            value={memberSearch}
            onChange={(event) => {
              const value = event.target.value
              setMemberSearch(value)
              if (!value.trim()) setMembers([])
            }}
            margin="normal"
            sx={{ maxWidth: 680, backgroundColor: '#fff' }}
          />
          <Alert severity={missingMemberPerformance ? 'warning' : 'info'} sx={{ my: 2 }}>
            Cargo y desempeño son opcionales. Se guardan al pulsar Guardar para las personas seleccionadas y se
            comparten con Personas y sus demás equipos. Para generar el 9Box, todas deben tener desempeño de 1 a 10,
            con hasta dos decimales. Mapas Descriptivos no lo requiere.
            {missingMemberPerformance > 0 && ` Falta el desempeño de ${missingMemberPerformance} ${missingMemberPerformance === 1 ? 'persona' : 'personas'}: el 9Box no se podrá generar hasta completarlo.`}
          </Alert>
          <Typography variant="body2" sx={{ mb: 1 }}>{form.memberIds.length} personas seleccionadas. La selección y los cambios se conservan al buscar.</Typography>
          {membersLoading && <Typography role="status">Buscando personas…</Typography>}
          <TableContainer sx={{ maxHeight: 'calc(100vh - 450px)', minHeight: 240, bgcolor: '#fff', border: '1px solid #d9e3ea', borderRadius: 1 }}>
            <Table size="small" stickyHeader>
              <TableHead><TableRow>
                <TableCell padding="checkbox">Selección</TableCell><TableCell>Persona y correo</TableCell>
                <TableCell>Documento</TableCell><TableCell>Cargo</TableCell><TableCell>Desempeño (opcional)</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {shownMembers.map((member) => <TableRow key={member.user_id} selected={form.memberIds.includes(member.user_id)}>
                  <TableCell padding="checkbox"><Checkbox checked={form.memberIds.includes(member.user_id)} disabled={saving}
                    inputProps={{ 'aria-label': `Seleccionar ${member.full_name}` }} onChange={() => toggleMember(member.user_id)} /></TableCell>
                  <TableCell>{member.full_name}<Typography variant="caption" display="block" color="text.secondary">{member.email || 'Sin correo'}</Typography></TableCell>
                  <TableCell>{member.document || 'Sin documento'}</TableCell>
                  <PersonFieldsCells name={member.full_name} cargo={memberCargoDrafts[member.user_id] || ''}
                    performance={memberPerformanceDrafts[member.user_id] || ''} disabled={saving || !form.memberIds.includes(member.user_id)}
                    onCargoChange={(value) => setMemberCargoDrafts((current) => ({ ...current, [member.user_id]: value }))}
                    onPerformanceChange={(value) => setMemberPerformanceDrafts((current) => ({ ...current, [member.user_id]: value }))} />
                </TableRow>)}
                {!shownMembers.length && !membersLoading && <TableRow><TableCell colSpan={5} sx={{ py: 4 }}>
                  {memberSearch.trim() ? 'No se encontraron personas elegibles.' : 'Escribe un nombre, correo o documento para buscar personas.'}
                </TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={() => setDialogOpen(false)}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={saving || invalidMemberPerformance || membersLoading}
            onClick={saveTeam}
            sx={{
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #c6016b 0%, #8f215d 100%)',
              textTransform: 'none',
            }}
          >
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
      {pendingAssociation && (
        <ManualEvaluationSelectionDialog
          people={pendingAssociation.people}
          choices={evaluationChoices}
          saving={associationSaving}
          onChoiceChange={(key, value) => setEvaluationChoices((current) => ({ ...current, [key]: value }))}
          onCancel={() => setPendingAssociation(null)}
          onConfirm={confirmEvaluations}
        />
      )}
      {processTeamOpen && <CreateTeamFromProcessesDialog onClose={() => setProcessTeamOpen(false)} onCreated={loadTeams} />}
    </MainLayout>
  )
}

export default LuminaAnalytics
